import { useEffect, useState } from "react";
import { pct, readPaperCard, type PaperCardRef } from "./cards";
import { useDataClient, type DeploymentAction } from "./client";
import { CloseIcon } from "./icons";

const money = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString(undefined, { maximumFractionDigits: 0 });

function signalText(s: PaperCardRef["lastSignal"]) {
  if (!s) return "No signals yet";
  return `${s.side === "sell" ? "Sell" : "Buy"} ${s.shares} ${s.symbol} · ${s.signal_date}`;
}

/** Refreshes a deployment's card data from the app, and on scheduler runs. */
function useLiveCard(initial: PaperCardRef): PaperCardRef {
  const client = useDataClient();
  const [card, setCard] = useState(initial);
  useEffect(() => {
    const load = () =>
      client
        .deployment(initial.deploymentId)
        .then((d) => {
          const c = d?.card && readPaperCard(d.card);
          if (c) setCard(c);
        })
        .catch(() => {});
    load();
    return client.onDeploymentUpdate(initial.deploymentId, load);
  }, [initial.deploymentId]);
  return card;
}

/** The chat card for a paper account: opens the pane. */
export function PaperCard({ card: initial, onOpen }: { card: PaperCardRef; onOpen: (id: string) => void }) {
  const card = useLiveCard(initial);
  const sub = [
    card.version != null ? `v${card.version}` : null,
    card.startDate ? `since ${card.startDate}` : null,
    card.status !== "active" ? card.status : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <button className="bt-card" onClick={() => onOpen(card.deploymentId)} title="Open the paper account">
      <span className="bt-card-kind paper">Paper</span>
      <span className="bt-card-main">
        <b>{card.strategyName || "Strategy"}</b>
        <span className="bt-card-sub">
          {sub} · {signalText(card.lastSignal)}
        </span>
      </span>
      <span className="bt-card-stat">
        {pct(card.returnSinceStart)}
        <span className="dim"> vs {card.benchmark ?? "bench"} {pct(card.benchmarkReturn)}</span>
      </span>
    </button>
  );
}

/** Right-side pane: Account · Signals · Settings (docs/041 §5). */
export function PaperPane({ deploymentId, onClose }: { deploymentId: string; onClose: () => void }) {
  const client = useDataClient();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState<"account" | "signals" | "settings">("account");
  const [busy, setBusy] = useState(false);

  const load = () =>
    client
      .deployment(deploymentId)
      .then(setD)
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    setD(null);
    setErr("");
    load();
    return client.onDeploymentUpdate(deploymentId, load);
  }, [deploymentId]);

  const r = d?.last_result ?? {};
  const set = async (action: DeploymentAction) => {
    setBusy(true);
    try {
      await client.setDeployment(deploymentId, action);
      await load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="bt-pane" aria-label="Paper account">
      <div className="bt-bar">
        <div className="bt-title">
          <b>{d?.strategy_name || "Paper account"}</b>
          <span className="dim">
            {d?.version != null ? ` v${d.version}` : ""} · paper{d?.status && d.status !== "active" ? ` · ${d.status}` : ""}
          </span>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close paper account">
          <CloseIcon />
        </button>
      </div>
      <div className="bt-tabs" role="tablist">
        {(["account", "signals", "settings"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
            {t === "account" ? "Account" : t === "signals" ? `Signals${d ? ` (${d.signals.length})` : ""}` : "Settings"}
          </button>
        ))}
      </div>
      <div className="bt-body">
        {err && <div className="bt-error">{err}</div>}
        {!d && !err && <div className="dim">Loading…</div>}
        {d && tab === "account" && (
          <>
            {r.status === "failed" && <div className="bt-error">Last run failed: {r.error}</div>}
            <div className="pa-tiles">
              <div><span className="dim">Account value</span><b>{money(r.portfolio_value)}</b><span className="dim">started {money(d.settings?.capital)}</span></div>
              <div><span className="dim">Return since {d.start_date}</span><b>{pct(r.return_since_start)}</b><span className="dim">{r.benchmark ?? "benchmark"} {pct(r.benchmark_return)}</span></div>
              <div><span className="dim">Cash</span><b>{money(r.cash)}</b><span className="dim">max drawdown {pct(r.max_drawdown)}</span></div>
            </div>
            <div className="bt-tablewrap">
              <table className="bt-table">
                <caption className="dim" style={{ textAlign: "left" }}>Open positions</caption>
                <thead><tr><th>Symbol</th><th>Shares</th><th>Cost</th><th>Last</th><th>P&L</th></tr></thead>
                <tbody>
                  {(r.positions ?? []).length === 0 && (
                    <tr><td colSpan={5} className="dim">No open positions</td></tr>
                  )}
                  {(r.positions ?? []).map((p: any) => {
                    const pl = p.cost_basis ? p.last_price / p.cost_basis - 1 : null;
                    return (
                      <tr key={p.symbol}>
                        <td>{p.symbol}</td><td>{p.shares}</td><td>{p.cost_basis?.toFixed(2)}</td><td>{p.last_price?.toFixed(2)}</td>
                        <td className={pl != null && pl >= 0 ? "up" : "down"}>{pct(pl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bt-foot dim">
              Simulated money. Signals fill at the next open with the backtest's costs. Last run {d.last_eval_at ?? "—"} UTC
              (data through {r.signal_date ?? "—"}). Runs daily after {d.run_after_utc} UTC while the app is open. Not investment advice.
            </div>
          </>
        )}
        {d && tab === "signals" && (
          <div className="bt-tablewrap">
            <table className="bt-table">
              <thead><tr><th>Date</th><th>Signal</th><th>Shares</th><th>Order</th></tr></thead>
              <tbody>
                {d.signals.length === 0 && <tr><td colSpan={4} className="dim">No signals yet</td></tr>}
                {d.signals.map((s: any, i: number) => (
                  <tr key={i}>
                    <td>{s.signal_date}</td>
                    <td className={s.side === "buy" ? "up" : "down"}>{s.side} {s.symbol}</td>
                    <td>{s.shares}</td>
                    <td className="dim">{s.details?.order_type ?? "market"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d && tab === "settings" && (
          <div className="pa-settings">
            <div><span className="dim">Status</span> {d.status}</div>
            <div><span className="dim">Version</span> v{d.version} (pinned)</div>
            <div><span className="dim">Data</span> {(d.settings?.yahoo_symbols ?? []).join(", ") || d.settings?.prices || "—"}</div>
            <div><span className="dim">Costs</span> {d.settings?.commission_bps} bps + {d.settings?.slippage_bps} bps slippage</div>
            <div><span className="dim">Runs</span> daily after {d.run_after_utc} UTC</div>
            {client.capabilities.controlDeployments && <div className="pa-actions">
              {d.status === "active" && <button disabled={busy} onClick={() => set("pause")}>Pause</button>}
              {d.status === "paused" && <button disabled={busy} onClick={() => set("resume")}>Resume</button>}
              {d.status !== "stopped" && (
                <button disabled={busy} className="danger" onClick={() => set("stop")}>Stop</button>
              )}
            </div>}
          </div>
        )}
      </div>
    </aside>
  );
}
